import type { Session, User } from '@supabase/supabase-js';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { getUiLang } from './i18n';
import { getPrefs, usePrefs } from './prefs';
import { supabase } from './supabase';
import type { Profile } from './types';

type AuthState = {
  ready: boolean;
  session: Session | null;
  user: User | null;
  profile: Profile | null;
  profileLoading: boolean;
  premium: boolean;
  /** user_interests'te en az bir satır var mı (mobil ile aynı tanım). */
  onboarded: boolean | null;
  /** Kartların okunacağı dil: profil > ziyaretçi tercihi > arayüz dili. */
  contentLang: string;
  refresh: () => Promise<void>;
  signOut: () => Promise<void>;
};

const Ctx = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const prefs = usePrefs();
  const [session, setSession] = useState<Session | null>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let alive = true;
    supabase.auth.getSession().then(({ data }) => {
      if (!alive) return;
      setSession(data.session);
      setReady(true);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_event, s) => {
      setSession(s);
      setReady(true);
    });
    return () => {
      alive = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  const uid = session?.user.id;

  const profileQ = useQuery({
    queryKey: ['profile', uid],
    enabled: !!uid,
    queryFn: async () => {
      const { data, error } = await supabase.from('profiles').select('*').eq('id', uid!).maybeSingle();
      if (error) throw error;
      return data as Profile | null;
    },
  });

  const premiumQ = useQuery({
    queryKey: ['premium', uid],
    enabled: !!uid,
    staleTime: 10 * 60_000,
    queryFn: async () => {
      // Sunucu Adapty'den doğrular; web'de ödeme yok, mobilde alınan abonelik okunur.
      await supabase.functions.invoke('sync-premium').catch(() => null);
      const { data } = await supabase.from('subscriptions').select('tier').eq('user_id', uid!).maybeSingle();
      return data?.tier === 'premium';
    },
  });

  const onboardedQ = useQuery({
    queryKey: ['onboarded', uid],
    enabled: !!uid,
    queryFn: async () => {
      const { count, error } = await supabase
        .from('user_interests')
        .select('subcategory_id', { count: 'exact', head: true })
        .eq('user_id', uid!);
      if (error) throw error;
      return (count ?? 0) > 0;
    },
  });

  const value = useMemo<AuthState>(() => {
    const profile = profileQ.data ?? null;
    return {
      ready,
      session,
      user: session?.user ?? null,
      profile,
      profileLoading: profileQ.isLoading,
      premium: premiumQ.data ?? false,
      onboarded: uid ? (onboardedQ.data ?? null) : null,
      contentLang: webContentLang(profile?.content_lang ?? prefs.contentLang ?? getUiLang()),
      refresh: async () => {
        await Promise.all([
          qc.invalidateQueries({ queryKey: ['profile', uid] }),
          qc.invalidateQueries({ queryKey: ['premium', uid] }),
          qc.invalidateQueries({ queryKey: ['onboarded', uid] }),
        ]);
      },
      signOut: async () => {
        await supabase.auth.signOut();
        qc.clear();
      },
    };
  }, [ready, session, uid, profileQ.data, profileQ.isLoading, premiumQ.data, onboardedQ.data, prefs.contentLang, qc]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthState {
  const v = useContext(Ctx);
  if (!v) throw new Error('useAuth outside AuthProvider');
  return v;
}

/**
 * Web Türkçe içerik göstermez (ürün kararı): mobilde içerik dili Türkçe olan
 * kullanıcı web'de arayüz diline, o da yoksa İngilizce'ye düşer.
 */
export function webContentLang(lang: string | null | undefined): string {
  if (!lang || lang === 'tr') {
    const ui = getUiLang();
    return ui;
  }
  return lang;
}

/** Oturum yokken de doğru dili veren yardımcı (sorgu anahtarlarında kullanılır). */
export function fallbackContentLang(): string {
  return webContentLang(getPrefs().contentLang ?? getUiLang());
}

export async function signInWithProvider(provider: 'google' | 'linkedin_oidc', next = '/today') {
  const redirectTo = `${location.origin}/auth/callback?next=${encodeURIComponent(next)}`;
  const { error } = await supabase.auth.signInWithOAuth({ provider, options: { redirectTo } });
  if (error) throw error;
}

export async function sendMagicLink(email: string, next = '/today') {
  const emailRedirectTo = `${location.origin}/auth/callback?next=${encodeURIComponent(next)}`;
  const { error } = await supabase.auth.signInWithOtp({
    email,
    options: { shouldCreateUser: true, emailRedirectTo },
  });
  if (error) throw error;
}
